import {
  getLocalisedText,
  isLoggedin as isLoggedInHelper,
} from "../../../app/helpers/helper";
import DiscoverIcon from "../../../assets/icons/DiscoverIcon";
import HomeIcon from "../../../assets/icons/HomeIcon";
import LogoutIcon from "../../../assets/icons/LogoutIcon";
import NotificationIcon from "../../../assets/icons/NotificationIcon";
import ProfileIcon from "../../../assets/icons/ProfileIcon";
import VerifiedProfile from "../../../assets/icons/VerifiedProfile";
import CirclePlusIcon from "../../../assets/icons/circle+.svg";
import DefaultProfileImage from "../../../assets/img/Profile.png";
import { PAGE } from "../../../app/constants";
import { SDK_CREDENTIAL_SIGNATURE_STORAGE_KEY } from "../../../app/constants/sdk";
import {
  FEED_TEMPLATE_TYPE_STORAGE_KEY,
  SDK_TEMPLATE_TYPE_STORAGE_KEY,
} from "../../../app/constants/template";
import { useConfig } from "../../../app/context/Config/configContext";
import { useLoginModalContext } from "../../../app/context/LoginModal";
import { clearSdkSessionStorage } from "../../../app/services/sdkSession";
import ShortsSdkVersion from "../../common/SDKVersion";
import gluedin from "gluedin-shorts-js";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Link } from "react-router-dom";

export const SidebarPersistenceContext = createContext(false);

type SidebarSessionCache = {
  initialized: boolean;
  profileHydrated: boolean;
  isLoggedin: boolean;
  userInfo: boolean;
  userDetail: any;
  userData: any;
  userMetaData: any;
};

const sidebarSessionCache: SidebarSessionCache = {
  initialized: false,
  profileHydrated: false,
  isLoggedin: false,
  userInfo: false,
  userDetail: null,
  userData: {},
  userMetaData: undefined,
};

const resetSidebarSessionCache = () => {
  sidebarSessionCache.initialized = false;
  sidebarSessionCache.profileHydrated = false;
  sidebarSessionCache.isLoggedin = false;
  sidebarSessionCache.userInfo = false;
  sidebarSessionCache.userDetail = null;
  sidebarSessionCache.userData = {};
  sidebarSessionCache.userMetaData = undefined;
};

function Sidebar(props: any) {
  const { t } = useTranslation();
  const location = useLocation();
  const usePersistentSidebar = useContext(SidebarPersistenceContext);
  const isPersistentRoot = props?.persistentRoot === true;
  const isSuppressed = usePersistentSidebar && !isPersistentRoot;
  const { showProfile = true } = props;
  const [isLoggedin, setIsLoggedin] = useState(sidebarSessionCache.isLoggedin);
  const navigate = useNavigate();
  const [userDetail, setUserDetail]: any = useState(sidebarSessionCache.userDetail);
  const [userInfo, setUserInfo]: any = useState(sidebarSessionCache.userInfo);
  const [editProfile, setEditProfile]: any = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [userData, setUserData]: any = useState(sidebarSessionCache.userData);
  const { setShowLoginModal } = useLoginModalContext();
  const { appConfig } = useConfig();
  const [userMetaData, setUserMetaData]: any = useState(
    sidebarSessionCache.userMetaData
  );

  useEffect(() => {
    if (isSuppressed) {
      return;
    }
    if (sidebarSessionCache.initialized) {
      return;
    }
    const accessToken = new gluedin.GluedInAuthModule().getAccessToken();
    accessToken
      .then((data: any) => {
        // handle success case
        if (data) {
          sidebarSessionCache.isLoggedin = true;
          setIsLoggedin(true);
        }
        sidebarSessionCache.initialized = true;
      })
      .catch((error: any) => {
        // handle error case
        console.error(error);
        sidebarSessionCache.initialized = true;
      });
  }, [isSuppressed]);

  useEffect(() => {
    if (isSuppressed) {
      return;
    }
    let isMounted = true;

    const syncAuthState = async () => {
      try {
        const accessToken = await new gluedin.GluedInAuthModule().getAccessToken();
        if (!isMounted) return;
        const loggedIn = Boolean(accessToken);
        if (loggedIn !== sidebarSessionCache.isLoggedin) {
          sidebarSessionCache.isLoggedin = loggedIn;
          if (!loggedIn) {
            sidebarSessionCache.profileHydrated = false;
            sidebarSessionCache.userInfo = false;
            sidebarSessionCache.userDetail = null;
            sidebarSessionCache.userData = {};
            sidebarSessionCache.userMetaData = undefined;
            setUserInfo(false);
            setUserDetail(null);
            setUserData({});
            setUserMetaData(undefined);
          }
          setIsLoggedin(loggedIn);
        }
      } catch (error) {
        console.error(error);
      }
    };

    syncAuthState();
    return () => {
      isMounted = false;
    };
  }, [isSuppressed, location.pathname]);

  useEffect(() => {
    if (isSuppressed) {
      return;
    }
    if (!isLoggedin) {
      return;
    }
    if (
      sidebarSessionCache.profileHydrated &&
      sidebarSessionCache.userInfo &&
      sidebarSessionCache.userDetail
    ) {
      return;
    }

    async function userDetails() {
      try {
        var userModuleObj = new gluedin.GluedInUserModule();
        let userBasicInfo: any = localStorage.getItem("userData");
        let userId = "";
        if (userBasicInfo && userBasicInfo !== undefined) {
          try {
            userBasicInfo = JSON.parse(userBasicInfo);
          } catch (parseError) {
            console.error(parseError);
            userBasicInfo = {};
          }
          userId = userBasicInfo?.userId || "";
          if (userId) {
            fetchMetaData(userId);
          }
          setUserData(userBasicInfo || {});
        }
        if (!userId) return;
        var userModuleResponse = await userModuleObj.getUserDetails(userId);
        if (userModuleResponse?.status === 200) {
          let userInfo = userModuleResponse?.data?.result;
          if (userInfo) {
            setUserInfo(true);
            setUserDetail(userInfo);
          }
        }
        sidebarSessionCache.profileHydrated = true;
      } catch (error) {
        console.error(error);
      }
    }
    async function fetchMetaData(userId: string) {
      if (!userId) return;
      try {
        var UserModuleObj = new gluedin.GluedInFeedModule();
        var userMetaData = await UserModuleObj.getMetadata({
          type: "storyOwner",
          ids: [userId],
        });
        if (userMetaData?.status === 200) {
          const userResponse = userMetaData.data.result;
          setUserMetaData(userResponse?.storyOwner[0] || []);
        }
      } catch (error) {
        console.error(error);
      }
    }
    userDetails();
  }, [isSuppressed, isLoggedin]);

  useEffect(() => {
    if (isSuppressed) {
      return;
    }
    sidebarSessionCache.isLoggedin = isLoggedin;
    sidebarSessionCache.userInfo = userInfo;
    sidebarSessionCache.userDetail = userDetail;
    sidebarSessionCache.userData = userData;
    sidebarSessionCache.userMetaData = userMetaData;
  }, [isSuppressed, isLoggedin, userInfo, userDetail, userData, userMetaData]);

  const loginModalHandler = () => {
    props?.loginHandler?.();
    setShowLoginModal(true);
  };

  const _navigateToProfile = () => {
    const accessToken = new gluedin.GluedInAuthModule().getAccessToken();
    accessToken
      .then((data: any) => {
        // handle success case
        if (!data) {
          // Show Login Modal
          loginModalHandler();
        } else {
          navigate("/my-profile");
        }
      })
      .catch((error: any) => {
        // handle error case
        console.error(error);
      });
  };

  const _navigateToNotification = () => {
    const accessToken = new gluedin.GluedInAuthModule().getAccessToken();
    accessToken
      .then((data: any) => {
        // handle success case
        if (!data) {
          // Show Login Modal
          //   setShowLoginModal(true);
          loginModalHandler();
        } else {
          navigate("/notification");
        }
      })
      .catch((error: any) => {
        // handle error case
        console.error(error);
      });
  };

  const toggleEditProfilePanel = () => {
    setEditProfile(true);
    setShowOverlay(true);
  };

  const handleStoryViewClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    event.preventDefault();
    const loggedIn = await isLoggedInHelper();
    if (!loggedIn) {
      setShowLoginModal(true);
      return;
    }
    window.location.assign(href);
  };

  const clearExperienceState = () => {
    clearSdkSessionStorage();
    localStorage.removeItem(SDK_CREDENTIAL_SIGNATURE_STORAGE_KEY);
    localStorage.removeItem(FEED_TEMPLATE_TYPE_STORAGE_KEY);
    localStorage.removeItem(SDK_TEMPLATE_TYPE_STORAGE_KEY);
    localStorage.removeItem("contentRoute");
  };

  const _logoutUser = (
    redirectPath: string = "/sign-in",
    options?: { resetExperience?: boolean }
  ) => {
    if (options?.resetExperience) {
      clearExperienceState();
    }
    localStorage.removeItem("following");
    localStorage.removeItem("userData");
    localStorage.removeItem("userDetails");
    setIsLoggedin(false);
    setUserInfo(false);
    setUserDetail(null);
    setUserData({});
    setUserMetaData(undefined);
    resetSidebarSessionCache();
    navigate(redirectPath);
  };

  const _changeExperience = async () => {
    try {
      const loggedIn = await isLoggedInHelper();
      if (loggedIn) {
        _logoutUser("/", { resetExperience: true });
        return;
      }
    } catch (error) {
      console.error(error);
    }

    clearExperienceState();
    navigate("/");
  };

  if (isSuppressed) {
    return null;
  }

  return (
    <>
      <div className="sidebar_wrapper">
        <div className="sidebar">
          {userInfo && showProfile && (
            <ul className="home-profile-info profile-page-head-ul list-none">
              <li className="profile-page-head-avatar">
                <div className="img-sec">
                  {userMetaData && userMetaData?.stories?.length > 0 ? (
                    <a
                      href={`story-view/${userMetaData?.userId}?type=${PAGE.PROFILE}`}
                      onClick={(event) =>
                        handleStoryViewClick(
                          event,
                          `story-view/${userMetaData?.userId}?type=${PAGE.PROFILE}`
                        )
                      }
                    >
                      <img
                        src={userDetail?.profileImageUrl}
                        alt=""
                        style={{
                          borderColor:
                            userMetaData?.stories?.length > 0
                              ? "#0033FF"
                              : "#fff",
                        }}
                      />
                    </a>
                  ) : (
                    <img
                      src={
                        userDetail?.profileImageUrl
                          ? userDetail.profileImageUrl
                          : DefaultProfileImage
                      }
                      id="profileImage"
                      className="bg-img-02 profileImage"
                      alt=""
                    />
                  )}
                </div>
              </li>
              <li className="home-head profile-page-head-content">
                <ul className="user-info profile-page-head-content-inner">
                  <li>
                    <h4
                      id="displayName"
                      className="flex items-center justify-center gap-2"
                    >
                      {getLocalisedText(userDetail, "fullName")}{" "}
                      {appConfig?.verifiedProfiles?.enabled &&
                        userData?.verified && <VerifiedProfile />}
                    </h4>
                    <h5 id="userId">{userDetail?.userName} </h5>
                    {/* <div className="desk-none">
                      <a href="javascript:void(0)" className="res-edit">
                        Edit
                      </a>
                    </div> */}
                  </li>
                </ul>
                <ul className="followers-info profile-page-head-content-inner">
                  <li>
                    <h5>{t("profile-followers")}</h5>
                    <span id="followerCount">
                      {userDetail?.followersCount ?? 0}
                    </span>
                  </li>
                  <li>
                    <h5>{t("profile-following")}</h5>
                    <span id="followingCount">
                      {userDetail?.followingCount ?? 0}
                    </span>
                  </li>
                  <li>
                    <h5>{t("profile-posts")}</h5>
                    <span id="videoCount">{userDetail?.videoCount ?? 0}</span>
                  </li>
                </ul>
              </li>
            </ul>
          )}
          <ul className="sidebar-ul">
            <li className={location.pathname === "/feed" ? "active" : ""}>
              <Link to="/feed">
                <HomeIcon />
                {t("home")}
              </Link>
            </li>

            <li className={location.pathname === "/discover" ? "active" : ""}>
              <Link to="/discover">
                <DiscoverIcon />
                {t("discover")}
              </Link>
            </li>

            <li
              className={location.pathname === "/notification" ? "active" : ""}
            >
              <button
                className="go-to-notification"
                onClick={_navigateToNotification}
              >
                <NotificationIcon />
                {t("notifications")}
              </button>
            </li>

            <li className={location.pathname === "/my-profile" ? "active" : ""}>
              <button
                className="go-to-user-profile"
                onClick={_navigateToProfile}
              >
                <ProfileIcon />
                {t("profile")}
              </button>
            </li>
            {isLoggedin && (
              <li onClick={() => _logoutUser()}>
                <button className="user-sign-out">
                  <LogoutIcon />
                  {t("text-logout")}
                </button>
              </li>
            )}
          </ul>
          {isLoggedin &&
            ((userData?.creator && appConfig?.inviteOnlyCreation) ||
              (!appConfig?.inviteOnlyCreation && appConfig?.ugcEnabled)) && (
              <Link
                to="/create-post"
                className="go-to-page-create-content content-btn"
              >
                {t("btn-createContent")}
                <img src={CirclePlusIcon} alt="" />
              </Link>
            )}
          <div className="sidebar-bottom-action">
            <button
              type="button"
              className="change-experience-btn"
              onClick={_changeExperience}
            >
              {t("change-experience")}
            </button>
          </div>
        </div>
      </div>

      <ShortsSdkVersion />

      {/* {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          handleClose={() => setShowLoginModal(false)}
        />
      )} */}
    </>
  );
}

export default Sidebar;
